import {
    Autocomplete,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
} from "@mui/material";
import { useFetch, useSnackbar, useTraitGroupDataTypes } from "../../../hooks";
import { useMemo, useState } from "react";

import { BACKEND_ROUTES } from "../../../backendRoutes";
import { LoadingButton } from "@mui/lab";
import { MODEL_PROPTYPES } from "../../../models";
import PropTypes from "prop-types";

export const AddTraitGroupDialog = ({
    open,
    onClose,
    experimentMission,
    experimentMissionTraitGroups,
    onSuccess,
}) => {
    const [selectedTraitGroupDataType, setSelectedTraitGroupDataType] =
        useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const { data: traitGroupDataTypesData, isLoading } =
        useTraitGroupDataTypes();
    const { put } = useFetch();
    const { openSnackbar } = useSnackbar();

    // Get list of existing trait group data type UUIDs to filter them out
    const existingTraitGroupDataTypeUuids = useMemo(
        () =>
            experimentMissionTraitGroups.map(
                ({ TraitGroupDataType }) => TraitGroupDataType.uuid
            ),
        [experimentMissionTraitGroups]
    );

    // Filter out trait groups that are already associated and match the acquisition vector's data types
    const availableTraitGroupDataTypes = useMemo(() => {
        if (!traitGroupDataTypesData?.rows || !experimentMission) return [];

        const sensorBundles =
            experimentMission.Mission?.AcquisitionVector?.SensorBundles;
        const acquisitionVectorDataTypes = sensorBundles
            ? sensorBundles.map((bundle) => bundle.Sensor.dataType)
            : [];

        // If no sensor bundles, return all trait groups that aren't already associated
        if (acquisitionVectorDataTypes.length === 0) {
            return traitGroupDataTypesData.rows.filter(
                (tgdt) => !existingTraitGroupDataTypeUuids.includes(tgdt.uuid)
            );
        }

        return traitGroupDataTypesData.rows.filter(
            (tgdt) =>
                !existingTraitGroupDataTypeUuids.includes(tgdt.uuid) &&
                acquisitionVectorDataTypes.includes(tgdt.dataType)
        );
    }, [
        traitGroupDataTypesData,
        experimentMission,
        existingTraitGroupDataTypeUuids,
    ]);

    const handleSubmit = async () => {
        if (!selectedTraitGroupDataType) return;

        setIsSubmitting(true);
        try {
            const body = {
                traitGroupDataTypeUuids: [selectedTraitGroupDataType.uuid],
            };

            const result = await put(
                `${BACKEND_ROUTES.EXPERIMENT_MISSION}/${experimentMission.uuid}/trait-groups`,
                {
                    body,
                }
            );

            if (result) {
                openSnackbar("Trait group added successfully", "success");
                setSelectedTraitGroupDataType(null);
                onSuccess();
                onClose();
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleClose = () => {
        setSelectedTraitGroupDataType(null);
        onClose();
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>Add Trait Group</DialogTitle>
            <DialogContent>
                <Autocomplete
                    autoFocus
                    sx={{ mt: 2 }}
                    options={availableTraitGroupDataTypes.sort((a, b) =>
                        -b.dataType.localeCompare(a.dataType) === 0
                            ? -b.TraitGroup.name.localeCompare(
                                  a.TraitGroup.name
                              )
                            : -b.dataType.localeCompare(a.dataType)
                    )}
                    groupBy={(option) => option.dataType}
                    getOptionLabel={(option) => option.TraitGroup.name}
                    isOptionEqualToValue={(option, value) =>
                        option.uuid === value.uuid
                    }
                    value={selectedTraitGroupDataType}
                    onChange={(_, newValue) => {
                        setSelectedTraitGroupDataType(newValue);
                    }}
                    loading={isLoading}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Select Trait Group"
                            placeholder="Choose a trait group to add"
                        />
                    )}
                    disabled={isSubmitting}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={isSubmitting}>
                    Cancel
                </Button>
                <LoadingButton
                    onClick={handleSubmit}
                    variant="contained"
                    loading={isSubmitting}
                    disabled={!selectedTraitGroupDataType}
                >
                    Add
                </LoadingButton>
            </DialogActions>
        </Dialog>
    );
};

AddTraitGroupDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    experimentMission: PropTypes.object.isRequired,
    experimentMissionTraitGroups: PropTypes.arrayOf(
        PropTypes.shape(MODEL_PROPTYPES.ExperimentMissionTraitGroup).isRequired
    ).isRequired,
    onSuccess: PropTypes.func.isRequired,
};
